/**
 * PHOTOS (docs/milestones/PHOTOS-contracts.md §4): the picture taken as a ranger falls - taken by the mission loop
 * (`DeathCamera`), kept on this device (IndexedDB), sent up under the save code, and shown by the menus through the
 * `PhotoPort`. This file puts the parts together; each runs in node under test on a Map and a fetch double.
 */

import type { PhotoPort } from '../../ui/screens/photoPort';
import type { FetchLike } from '../cloudSave/types';
import { createDeathCamera, type Developing } from './camera';
import { encodeOnCanvas } from './canvasEncode';
import { cryptoBytes } from './encode';
import { openPhotoBackend } from './idb';
import { createPhotoPort } from './port';
import { createPhotoStore, type PhotoBackend } from './store';
import { createPhotoSync, type PhotoCloud, syncAfterEverySave } from './sync';
import { createPhotoTransport } from './transport';
import type { DeathCamera } from './types';

export { cameraFor, PHOTO_MODES } from './camera';
export { cropFrame, encodeWithin, jpegQualities, newPhotoId, PHOTO_ID_LENGTH } from './encode';
export { createPhotoStore, memoryBackend } from './store';
export type { PhotoBackend, PhotoRecord, PhotoStore } from './store';
export type { PhotoCloud } from './sync';

export interface Photos {
  /** Handed to `startMission` for an operation or Classic deployment (`cameraFor`). */
  camera: DeathCamera;
  /** Handed to the menus as `ports.photos`. */
  port: PhotoPort;
  /** Resolves when no picture is being developed and no upload is running: for the tests. */
  whenIdle(): Promise<void>;
}

export interface PhotosDeps {
  cloud: PhotoCloud;
  fetch: FetchLike;
  origin: string;
  /** IndexedDB (`openPhotoBackend`), a Map under test, or null where there is none: photos off. */
  backend: PhotoBackend | null | Promise<PhotoBackend | null>;
  /** Frame -> JPEG; the canvas (`encodeOnCanvas`) unless a test says otherwise. */
  encode?: (rgba: Uint8ClampedArray) => Promise<Blob | null>;
  randomBytes?: (count: number) => Uint8Array;
  now?: () => number;
  objectUrl?: (jpeg: Blob) => string;
}

/** The parts, from their dependencies: what the tests drive. */
export function createPhotos(deps: PhotosDeps): Photos {
  const store = createPhotoStore(deps.backend);
  const transport = createPhotoTransport(deps.fetch, deps.origin);
  const sync = createPhotoSync(deps.cloud, store, transport);
  const developing: Developing = new Map();
  const now = deps.now ?? (() => Date.now());
  const camera = createDeathCamera({
    store,
    encode: deps.encode ?? encodeOnCanvas,
    randomBytes: deps.randomBytes ?? cryptoBytes,
    now,
    developing,
    kept: () => sync.kept(),
  });
  const port = createPhotoPort({
    store, transport, cloud: deps.cloud, origin: deps.origin, developing, now,
    objectUrl: deps.objectUrl ?? ((jpeg) => URL.createObjectURL(jpeg)),
  });
  syncAfterEverySave(deps.cloud, sync);
  return {
    camera,
    port,
    async whenIdle() {
      while (developing.size > 0) await Promise.all(developing.values());
      await sync.whenIdle();
    },
  };
}

/**
 * The camera and the port for this page, made once next to `startWall(cloud)`. Rides on the cloud layer for the
 * code and the moments a save is acknowledged; with cloud saves off the pictures stay on this device.
 */
export function startPhotos(cloud: PhotoCloud, fetchLike?: FetchLike, origin?: string, backend?: PhotoBackend | null): Photos {
  return createPhotos({
    cloud,
    fetch: fetchLike ?? ((input, init) => fetch(input, init)),
    origin: origin ?? globalThis.location?.origin ?? '',
    backend: backend === undefined ? openPhotoBackend() : backend,
  });
}
